import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { MdEmail, MdCheckCircle, MdError } from "react-icons/md";
import emailjsConfig from "../config/emailjsConfig";

const INITIAL = { name: "", email: "", inquiry: "", subject: "", message: "" };

const INQUIRY_TYPES = [
  "Full-Time Job Opportunity",
  "Freelance Project",
  "Consulting Engagement",
  "Collaboration / Partnership",
  "General Inquiry",
];

export default function Contact() {
  const formRef = useRef(null);
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const submittingRef = useRef(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (submittingRef.current) return; // hard block double submit
    submittingRef.current = true;
    setLoading(true);
    setStatus(null);
    try {
      // 1. Send notification email to you
      await emailjs.sendForm(
        emailjsConfig.serviceId,
        emailjsConfig.templateId,
        formRef.current,
        emailjsConfig.publicKey
      );
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      console.error("Notification email failed:", err?.text ?? err);
      setStatus("error");
      setLoading(false);
      return;
    }

    try {
      // 2. Send auto-reply to the user
      await emailjs.send(
        emailjsConfig.serviceId,
        emailjsConfig.autoReplyTemplateId,
        {
          name:    form.name,
          email:   form.email,
          inquiry: form.inquiry,
          subject: form.subject,
          message: form.message,
        },
        emailjsConfig.publicKey
      );
    } catch (err) {
      // Auto-reply failure — log it but don't show error to user
      // since their main message was already received
      console.error("Auto-reply failed:", err?.text ?? err);
      console.error("Auto-reply template ID:", emailjsConfig.autoReplyTemplateId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-navy-950 section-padding">
      <div className="container-max">
        <div className="text-center mb-14">
          <span className="section-tag">Get In Touch</span>
          <h2 className="section-title">Contact Me</h2>
          <p className="section-sub">
            Available for{" "}
            <span className="text-cyan-400 font-semibold">
              full-time roles, freelance projects, and consulting engagements
            </span>{" "}
            in Jamaica or fully remote. I will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">

          {/* Info panel */}
          <div className="flex flex-col gap-5">
            <div className="card">
              <h3 className="font-bold text-white mb-5 text-base">Let's connect</h3>
              <div className="space-y-4">
                <ContactRow
                  icon={<MdEmail className="text-cyan-400" size={18} />}
                  label="Email"
                  value="nakardarichards3@gmail.com"
                  href="mailto:nakardarichards3@gmail.com"
                />
                <ContactRow
                  icon={<FaLinkedin className="text-cyan-400" size={16} />}
                  label="LinkedIn"
                  value="linkedin.com/in/nakarda"
                  href="https://www.linkedin.com/in/nakarda/"
                />
                <ContactRow
                  icon={<FaGithub className="text-cyan-400" size={16} />}
                  label="GitHub"
                  value="github.com/NakardaRichards"
                  href="https://github.com/NakardaRichards"
                />
              </div>
            </div>

            <div className="card border-cyan-500/20 bg-cyan-500/5">
              <p className="text-xs font-semibold text-cyan-400 uppercase tracking-widest mb-2">
                Location & Availability
              </p>
              <p className="text-sm text-slate-300 leading-relaxed">
                Based in{" "}
                <span className="text-white font-semibold">Clarendon, Jamaica</span>.
                Open to on-site roles locally and fully remote opportunities worldwide.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {["Full-Time", "Freelance", "Consulting", "Remote"].map((tag) => (
                  <span key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="card">
            {status === "success" && (
              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30
                              text-emerald-400 rounded-xl px-4 py-3 mb-5 text-sm">
                <MdCheckCircle size={17} /> Message sent! I will be in touch soon.
              </div>
            )}
            {status === "error" && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30
                              text-red-400 rounded-xl px-4 py-3 mb-5 text-sm">
                <MdError size={17} /> Something went wrong. Please try again.
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              <Field label="Full Name" name="name" type="text"
                placeholder="Jane Smith" value={form.name} onChange={handleChange} />
              <Field label="Email Address" name="email" type="email"
                placeholder="jane@company.com" value={form.email} onChange={handleChange} />

              {/* Inquiry type dropdown */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Inquiry Type <span className="text-cyan-400">*</span>
                </label>
                <select name="inquiry" value={form.inquiry} onChange={handleChange} required
                  className="input-field appearance-none cursor-pointer">
                  <option value="" disabled>Select inquiry type...</option>
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <Field label="Subject" name="subject" type="text"
                placeholder="Brief subject line..."
                value={form.subject} onChange={handleChange} />

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Message <span className="text-cyan-400">*</span>
                </label>
                <textarea name="message" value={form.message} onChange={handleChange}
                  required rows={5}
                  placeholder="Tell me about your project, role, or opportunity..."
                  className="input-field resize-none" />
              </div>

              <button type="submit" disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Sending...
                  </>
                ) : "Send Message"}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type, placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-400 mb-1.5">
        {label} <span className="text-cyan-400">*</span>
      </label>
      <input type={type} name={name} value={value} onChange={onChange}
        required placeholder={placeholder} className="input-field" />
    </div>
  );
}

function ContactRow({ icon, label, value, href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 group">
      <div className="w-9 h-9 bg-cyan-500/10 border border-cyan-500/20 rounded-lg
                      flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-medium text-slate-300 group-hover:text-cyan-400 transition-colors">
          {value}
        </p>
      </div>
    </a>
  );
}